import { format } from 'date-fns';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  getProductEvent,
  getProductStatistic,
  Result,
} from '../../api/api';
import { IProductEvent, IProductStatistic } from '../../api/types';
import { Section } from '../Common/Section';
import { DevicesPerFirmwareGraph } from './DevicesPerFirmwareGraph';
import { DevicesPerFirmwareTable } from './DevicesPerFirmwareTable';
import { VariantDropdown } from './VariantDropdown';
import { DropdownMenu } from '../DropdownMenu/DropdownMenu';
import { useDataRefresh } from '../Hooks/useDataRefresh';

export type ProductParams = {
  productCode: string;
  variant: string;
};

export const Product = () => {
  const { productCode = '', variant = '' } =
    useParams<ProductParams>();
  const [statistic, setStatistic] = useState<
    Result<IProductStatistic[]>
  >({ status: 'Loading' });
  const [refreshCounter, triggerRefresh] = useDataRefresh();
  const [events, setEvents] = useState<Result<IProductEvent[]>>({
    status: 'Loading',
  });
  const [eventRefreshError, setEventRefreshError] = useState<
    string | undefined
  >();
  const [statisticRefreshError, setStatisticRefreshError] = useState<
    string | undefined
  >();
  const loadedProductKeyRef = useRef('');
  const loadedEventsRef = useRef(false);

  const loadEvents = useCallback(
    async (showLoading = false) => {
      if (showLoading) {
        setEvents({ status: 'Loading' });
      }
      setEventRefreshError(undefined);

      const result = await getProductEvent(productCode, variant);
      if (result.status === 'OK') {
        loadedEventsRef.current = true;
        setEvents(result);
      } else if (showLoading || !loadedEventsRef.current) {
        setEvents(result);
      } else {
        setEventRefreshError(
          getResultErrorMessage(
            result,
            'Could not refresh product events.',
          ),
        );
      }
    },
    [productCode, variant],
  );

  const loadProduct = useCallback(
    async (forceLoading = false) => {
      const productKey = `${productCode}/${variant}`;
      const showLoading =
        forceLoading || loadedProductKeyRef.current !== productKey;

      if (showLoading) {
        setStatistic({ status: 'Loading' });
        setEvents({ status: 'Loading' });
        loadedEventsRef.current = false;
      }

      setStatisticRefreshError(undefined);
      setEventRefreshError(undefined);
      const result = await getProductStatistic(productCode, variant);

      if (result.status !== 'OK') {
        if (showLoading) {
          setStatistic(result);
        } else {
          setStatisticRefreshError(getResultErrorMessage(result));
        }
        return;
      }

      setStatistic(result);
      loadedProductKeyRef.current = productKey;
      await loadEvents(showLoading);
    },
    [loadEvents, productCode, variant],
  );

  useEffect(() => {
    queueMicrotask(() => {
      void loadProduct();
    });
  }, [loadProduct, refreshCounter]);

  if (statistic.status === 'OK') {
    const hasFirmwareStatistics = statistic.data.length > 0;

    return (
      <>
        <div className="inset-x-0 w-full top-0 h-16">
          <DropdownMenu />
        </div>

        <div className="mt-10 md:container md:mx-auto">
          <VariantDropdown />
          {statisticRefreshError && (
            <div
              className="m-2 flex items-center justify-between gap-4 border border-red-200 bg-red-50 p-3 text-red-800"
              role="alert"
            >
              <span>
                Could not refresh product data.{' '}
                {statisticRefreshError}
              </span>
              <button
                type="button"
                onClick={() => void loadProduct()}
                className="text-sm font-medium underline"
              >
                Retry
              </button>
            </div>
          )}
          <DevicesPerFirmwareGraph
            productStatistic={statistic.data}
            productCode={productCode}
            variant={variant}
            succesfullyUploadedCallback={triggerRefresh}
          />
          {hasFirmwareStatistics ? (
            <DevicesPerFirmwareTable
              productStatistic={statistic.data}
              productCode={productCode}
              variant={variant}
            />
          ) : (
            <Section>
              <div role="status">
                No firmware has been uploaded for this product yet.
              </div>
            </Section>
          )}
          <Section>
            <div>
              {events.status !== 'OK' &&
                events.status !== 'Loading' && (
                  <div
                    className="flex items-center gap-3 text-sm text-red-600"
                    role="alert"
                  >
                    <span>
                      Could not load product events.{' '}
                      {getResultErrorMessage(
                        events,
                        'Could not load product events.',
                      )}
                    </span>
                    <button
                      type="button"
                      onClick={() => void loadEvents()}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Retry events
                    </button>
                  </div>
                )}
              {events.status === 'OK' &&
                events.data.map((e) => {
                  const time = format(
                    new Date(e.timestamp),
                    'dd-MM-yyyy HH:mm:ss xxx',
                  );
                  return (
                    <div
                      key={e.id ?? `${e.timestamp}|${e.description}`}
                      className="flex flex-row text-sm font-mono"
                    >
                      <div
                        className="pr-5"
                        title={e.timestamp}
                        aria-label={`Event time ${time}`}
                      >
                        {time}
                      </div>
                      <div>{e.description}</div>
                    </div>
                  );
                })}
              {eventRefreshError && events.status === 'OK' && (
                <div
                  className="flex items-center gap-3 text-sm text-red-600"
                  role="alert"
                >
                  <span>{eventRefreshError}</span>
                  <button
                    type="button"
                    onClick={() => void loadEvents()}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    Retry events
                  </button>
                </div>
              )}
            </div>
          </Section>
        </div>
      </>
    );
  }

  if (statistic.status === 'Loading') {
    return <div role="status">Loading product data...</div>;
  }

  if (statistic.status === 'Forbidden') {
    return (
      <div role="alert">
        You don't have permission to view this product.
      </div>
    );
  }

  return (
    <div role="alert">
      <div>
        {statistic.status}:{' '}
        {'errorMessages' in statistic
          ? statistic.errorMessages.join(', ')
          : 'Could not load product statistics.'}
      </div>
      <button
        type="button"
        onClick={() => void loadProduct(true)}
        className="text-sm text-blue-600 hover:text-blue-800"
      >
        Retry
      </button>
    </div>
  );
};

function getResultErrorMessage(
  result: Result<unknown>,
  fallback = 'Could not load product statistics.',
) {
  return 'errorMessages' in result
    ? result.errorMessages.join(', ')
    : fallback;
}
